// @ts-nocheck
import { DashboardLayout } from "./Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, Trash2, Users, Home } from "lucide-react";
import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import MyDialogContent from "@/components/MyDialogContent";
import RHFWrapper from "@/components/RHFWrapper";
import RhfSelect from "@/components/rhfFields/RhfSelect";
import RhfInput from "@/components/rhfFields/RhfInput";
import AxiosInstance from "@/utils/AxiosInstance";
import { useAuth } from "@/contexts/use-auth";
import {
  formatPlanStatusLabel,
  getPromise,
  getPromiseToast,
  getStatusBadge,
  getTableValue,
} from "@/utils/helpers";
import { useGetData } from "@/hooks/useGetData";
import LoaderWrapper from "./LoaderWrapper";
import { HorizontalCardShimmer } from "@/components/Shimmer";
import * as yup from "yup";
import { Badge } from "@/components/ui/badge";

interface AccessGrant {
  id: string;
  name: string;
  email: string;
  type: "family" | "homecare";
  grantedDate: string;
}

function AccessStatusBadge({ status }: { status?: string }) {
  const obj = getStatusBadge(status);
  const label = formatPlanStatusLabel(String(status ?? ""));
  return (
    <Badge
      variant="outline"
      className="capitalize py-1 px-2 rounded-full teachers text-xs"
      style={{
        backgroundColor: obj.bgColor,
        color: obj.mainColor,
        borderColor: obj.mainColor,
      }}
    >
      {label || "—"}
    </Badge>
  );
}

export default function PatientManageAccess() {
  const [accessGrants, setAccessGrants] = useState<AccessGrant[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { user } = useAuth();
  const [btnLoading, setBtnLoading] = useState(false);
  const currentUserEmail = (user?.email ?? "").trim().toLowerCase();
  let {
    apiData: grantedAccessData,
    loading,
    err,
    fetchData,
  } = useGetData(
    "/portal-access/granted",
    { patientId: user?._id },
    false,
    true,
  );
// console.log("asdf",grantedAccessData?.data?.accesses)
  let grantedAccess = grantedAccessData?.data?.accesses ||  [
    {
      id: "1",
      fullName: "John Doe",
      email: "t0Q9o@example.com",
      type: "family_friend",
      grantedDate: "2022-01-01",
    },
  ];
  const handleGrantAccess = async (formdata) => {
    // e.preventDefault();
    // const formData = new FormData(e.currentTarget);
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
      setBtnLoading(true);
      const { data } = await AxiosInstance().post("/portal-access/grant", {
        patientId: user?._id,
        ...formdata,
      });
      // console.log(data);
      resolveFn("Access granted successfully");
      fetchData();
      setIsDialogOpen(false)
    } catch (error) {
      rejectFn(error);
      console.log(error.message);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleRevokeAccess = async (id: string) => {
    // Browser confirmation box
    const ok = window.confirm("Are you sure you want to revoke access?");
    if (!ok) return; // user cancelled → stop here

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      const { data } = await AxiosInstance().delete(
        `/portal-access/revoke/${id}`,
      );
      resolveFn("Access revoked successfully");
      fetchData();
    } catch (error: any) {
      rejectFn(error);
      console.log(error?.message);
    }
  };

  return (
    <DashboardLayout activePage="/patient-manage-access">
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Manage Access</h2>
          <p className="text-muted-foreground mt-2">
            Grant portal access to family, friends, or home care providers
          </p>
        </div>

        <div className="flex justify-end">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="w-4 h-4 mr-2" />
                Grant Access
              </Button>
            </DialogTrigger>
            <MyDialogContent heading="Grant Portal Access">
              <RHFWrapper
                initialValues={{}}
                isValidate={true}
                onSubmit={handleGrantAccess}
                validateSchema={yup.object({
                  accessType: yup.string().required("Access type is required"),
                  fullName: yup.string().required("Name is required").trim(),
                  email: yup
                    .string()
                    .required("Email is required")
                    .email("Email must be valid")
                    .trim()
                    .test(
                      "not-current-user-email",
                      "You can't use your own email address",
                      (value) => {
                        if (!value) return true;
                        if (!currentUserEmail) return true;
                        return value.trim().toLowerCase() !== currentUserEmail;
                      },
                    ),
                })}
                className="space-y-3"
              >
                <RhfSelect
                  name="accessType"
                  label="Access Type"
                  required
                  options={[
                    { label: "Family Friend", value: "family_friend" },
                    { label: "Home Care", value: "home_care" },
                  ]}
                />
                <RhfInput
                  name="fullName"
                  label="Full Name"
                  isAlphabet
                  required
                />
                <RhfInput
                  name="email"
                  label="Email Address"
                  type="email"
                  required
                />
                <div className="mt-3 flex gap-2 items-center justify-end">
                  <Button
                    type="button"
                    variant={"outline"}
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={btnLoading}>
                    Grant Access
                  </Button>
                </div>
              </RHFWrapper>
            </MyDialogContent>
            {/* <DialogContent>
              <DialogHeader>
                <DialogTitle>Grant Portal Access</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleGrantAccess} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Access Type</Label>
                  <Select name="type" defaultValue="family" required>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="family_friend">Family & Friends</SelectItem>
                      <SelectItem value="home_care">Home Care</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input id="name" name="name" required placeholder="Enter full name" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input id="email" name="email" type="email" required placeholder="Enter email address" />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">Grant Access</Button>
                </div>
              </form>
            </DialogContent> */}
          </Dialog>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Family & Friends
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LoaderWrapper loading={loading} error={err} customLoaderComponent={<HorizontalCardShimmer count={2}/>}>

              {Array.isArray(grantedAccess) &&
              grantedAccess.filter((g) => g.accessType === "family_friend").length >
                0 ? (
                <div className="space-y-3">
                  {grantedAccess
                    .filter((g) => g.accessType === "family_friend")
                    .map((grant, index) => (
                      <motion.div
                        key={grant._id || grant.id || index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="p-4 border rounded-lg"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                            <p className="font-semibold">{grant?.grantedToUserId?.name}</p>
                            <AccessStatusBadge status={grant?.status} />
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {grant?.grantedToUserId?.email}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Granted: {getTableValue( grant?.createdAt)}
                            </p>
                          </div>
                         
                         {
                          grant?.status !== 'revoked' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              handleRevokeAccess(grant._id || grant.id)
                            }
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                         )}
                        </div>
                      </motion.div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No family or friends have been granted access yet
                </p>
              )}
              </LoaderWrapper>

            </CardContent>

           
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="w-5 h-5" />
                Home Care
              </CardTitle>
            </CardHeader>
            <CardContent>
            <LoaderWrapper loading={loading} error={err} customLoaderComponent={<HorizontalCardShimmer count={2}/>}>
              {grantedAccess.filter((g) => g.accessType === "home_care").length > 0 ? (
                <div className="space-y-3">
                  {grantedAccess
                    .filter((g) => g.accessType === "home_care")
                    .map((grant, index) => (
                      <motion.div
                      key={grant._id || grant.id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 border rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                          <p className="font-semibold">{grant?.grantedToUserId?.name}</p>
                          <AccessStatusBadge status={grant?.status} />
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {grant?.grantedToUserId?.email}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Granted: {getTableValue( grant?.createdAt)}
                          </p>
                        </div>

                        {
                          grant?.status !== 'revoked' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleRevokeAccess(grant._id || grant.id)
                          }
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        )}
                      </div>
                    </motion.div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No home care providers have been granted access yet 
                </p>
              )}
              </LoaderWrapper>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <h3 className="font-semibold">About Portal Access</h3>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>
                  Granted users will receive an email invitation to access your
                  portal
                </li>
                <li>
                  They can view your care plan, appointments, and health
                  information
                </li>
                <li>You can revoke access at any time</li>
                <li>All access activity is logged for your security</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
